<script setup lang="ts">
import { PropType } from "vue";
import { DirectLink, Participant, Project, Recipients, Trainer } from "../../types/PersologOnlineTypes.ts";
import { copyToClipboard } from "../../utils/GeneralUtils.ts";
import SendOrRemindAll from "./activation/SendOrRemindAll.vue";
import ProfileZipDownload from "../profiles/ProfileZipDownload.vue";
import { useI18n } from "vue-i18n";
import AddParticipants from "./AddParticipants.vue";
import { productHasBenchmarkDiagram } from "../../utils/ProjectUtils";
import BuyButton from "../common/BuyButton.vue";
import { ProfileAdministrationAPI } from "../../api";
import ProfileReleaseButton from "../common/ProfileReleaseButton.vue";
import { useProjectDetailsDirectLinkStore } from "../../stores/projectDetailsDirectLink";

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        default: null
    },
    participants: {
        type: Array as PropType<Participant[]>,
        required: true
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true
    },
    productAvailableQuantity: {
        type: Number, // Or the appropriate type
        default: 0
    },
    productName: {
        type: String,
        required: true
    }
});

const emit = defineEmits(["update:participants", "update:project", "update:quantity"]);

const { t } = useI18n();
const directLinkStore = useProjectDetailsDirectLinkStore();
const persologBaseUrl = import.meta.env.VITE_APP_PERSOLOG_URL || "https://persolog.online";
const questionnairePath = import.meta.env.VITE_APP_QUESTIONNAIRE_FRONTEND_PATH || "/questionnaire";

function composeDirectLink() {
    return `${persologBaseUrl}${questionnairePath}/direct-link/${props.directLink?.hash}?lang=${props.project.language}`;
}

function callToAction(): string {
    if (props.project.deletionDate !== null && props.project.deletionDate !== undefined) {
        return "PROJECT_ARCHIVED";
    }
    if (productHasBenchmarkDiagram(props.project.product)) {
        return "BENCHMARK_DIAGRAM";
    }
    if (props.directLink && props.participants.length === 0) {
        return "DIRECT_LINK_EMPTY";
    }
    if (props.directLink === null && props.participants.length === 0) {
        return "PARTICIPANTS_EMPTY";
    }
    if (props.participants.some((p) => p.status && ["GENERATED"].includes(p.status))) {
        return "PARTICIPANTS_GENERATED";
    }
    if (props.participants.some((p) => p.status && ["GENERATED", "NOTIFIED"].includes(p.status))) {
        return "PARTICIPANTS_GENERATED_OR_NOTIFIED";
    }
    if (props.productAvailableQuantity === 0) {
        return "PROFILE_QUANTITY_EMPTY";
    } else {
        return "PARTICIPANTS_CREATED";
    }
}

function copyDirectLink() {
    copyToClipboard(composeDirectLink());
}

function isDirectLinkAllowed() {
    return props.participants.every((participant) => participant.status !== "CREATED");
}

function openCreateDirectLink() {
    directLinkStore.openCreateDialog(isDirectLinkAllowed(), props.trainer.id, props.project.defaultProduct);
}

function composeEmail() {
    const ccEmail = props.project.additionalRecipient?.email ?? "";
    const subject = t("project-details.participants.callForAction.email.subject", {
        project: props.project?.name
    });
    const body = t("project-details.participants.callForAction.email.body", {
        directLink: composeDirectLink(),
        trainerName: `${props.trainer?.firstname} ${props.trainer?.lastname}`
    });
    globalThis.location.href = `mailto:?cc=${ccEmail}&subject=${subject}&body=${encodeURIComponent(body)}`;
}

async function reactivateProject() {
    const project = await ProfileAdministrationAPI.reactivateProject(props.project?.id);
    emit("update:project", project);
}
</script>
<template>
    <div class="call-to-action-container">
        <BuyButton
            v-if="callToAction() !== 'BENCHMARK_DIAGRAM'"
            :product="project.product"
            :product-name="productName"
            :trainer="trainer"
            :with-label="true"
            with-classes="flex-item"
            @update="emit('update:quantity')"
        />
        <SendOrRemindAll
            v-if="callToAction() === 'PARTICIPANTS_CREATED'"
            with-classes="flex-item"
            with-label
            :trainer="trainer"
            :participants="participants"
            :project="project"
            :product-name="productName"
            :quantity="productAvailableQuantity"
            @update:participants="(p) => emit('update:participants', p)"
        />
        <ProfileReleaseButton
            withLabel
            :trainer="trainer"
            :project="project"
            v-if="callToAction() === 'PARTICIPANTS_GENERATED' && project.recipients == Recipients.TRAINER"
            @released-participants="(p) => emit('update:participants', p)"
        />
        <ProfileZipDownload
            v-if="(callToAction() === 'PARTICIPANTS_GENERATED_OR_NOTIFIED' || callToAction() === 'PARTICIPANTS_GENERATED') && project.recipients !== Recipients.PARTICIPANT"
            with-classes="flex-item"
            with-label
            :participants="participants"
            :project="project"
        />
        <AddParticipants
            with-label
            with-classes="flex-item"
            :project="project"
            :participants="participants"
            v-if="callToAction() === 'PARTICIPANTS_EMPTY'"
            @update:participants="(p) => emit('update:participants', p)"
        />
        <Button
            class="cta-action-button"
            v-if="!directLink && callToAction() !== 'PROJECT_ARCHIVED' && callToAction() !== 'BENCHMARK_DIAGRAM'"
            @click="openCreateDirectLink"
            icon="pi pi-link"
            :label="t('project-configuration.directLink.create')"
        />
        <Button
            class="cta-action-button"
            v-if="callToAction() === 'DIRECT_LINK_EMPTY'"
            @click="copyDirectLink"
            icon="pi pi-copy"
            :label="t('project-details.participants.callForAction.copy')"
        />
        <Button
            class="cta-action-button"
            v-if="callToAction() === 'DIRECT_LINK_EMPTY'"
            @click="composeEmail"
            icon="pi pi-envelope"
            :aria-label="t('accessibility.buttons.sendEmail')"
        />
        <Button
            class="cta-action-button"
            v-if="callToAction() === 'PROJECT_ARCHIVED'"
            @click="reactivateProject"
            icon="pi pi-history"
            :label="t('project-details.participants.callForAction.reactivate')"
        />
    </div>
</template>

<style scoped>
.call-to-action-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: flex-start;
}

@media screen and (max-width: 768px) {
    .call-to-action-container {
        flex-direction: column;
        align-items: stretch;
    }

    .cta-action-button {
        width: 100%;
        justify-content: center;
    }
}
</style>
